import Modal from "@/components/common/modal";
import { TextAreaField } from "@/components/common/text-field";
import { DialogFooter } from "@/components/ui/dialog";
import { useDeactivateInsolvency } from "@/queries/users/insolvencies.api";
import {
  type CancelInfractionFormData,
  schema,
} from "@/schemas/insolvencies/cancel-infraction";
import { cn } from "@/utils/theme";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

interface Props {
  infractionId: string | null;
  isOpen: boolean;
  onClose: () => void;
  reason: string;
}

const CancelInfractionModal = ({
  infractionId,
  isOpen,
  onClose,
  reason,
}: Props) => {
  const deactivateInsolvency = useDeactivateInsolvency(infractionId || "");
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<CancelInfractionFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      comments: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data: CancelInfractionFormData) => {
    if (!infractionId) return;

    try {
      await deactivateInsolvency.mutateAsync({
        status: "CANCELLED",
        comments: data.comments,
        reason: reason || "",
      });
      showToast({
        title: "Infracción anulada",
        description: "La infracción ha sido anulada correctamente.",
      });
      handleClose();
    } catch (e) {
      console.error("Error cancelling infraction:", e);
      showToast({
        title: "Error al anular la infracción",
        description: "Ocurrió un error al intentar anular la infracción.",
        options: {
          type: "error",
        },
      });
    }
  };

  const handleClose = () => {
    reset({
      comments: "",
    });
    clearErrors();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Anular infracción"
      description="¿Estás seguro de que deseas anular esta infracción? Esta acción no puede ser desecha."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        id="cancel-infraction-form"
      >
        <TextAreaField
          label="¿Por qué deseas anular esta infracción?"
          required
          placeholder="Escribe tu respuesta..."
          className="relative h-[88px] max-h-[88px]"
          {...register("comments")}
          error={errors.comments?.message}
        />
        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full rounded-lg"
              variant="tertiary"
              onClick={handleClose}
              disabled={deactivateInsolvency.isPending}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              variant="danger-secondary"
              className="w-full rounded-lg"
              type="submit"
              disabled={!isValid || deactivateInsolvency.isPending}
            >
              Anular
              {deactivateInsolvency.isPending && (
                <Icon
                  type="far"
                  name={"spinner-third"}
                  className={cn("text-current", "animate-spin")}
                />
              )}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default CancelInfractionModal;
